import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// --- Bromocriptine --------------------------------------------------

const bromocriptine: ContentBlock<"medication"> = {
  id: makeId("med-bromocriptine"),
  kind: "medication",
  tags: ["medications", "her", "pre-cycle", "evidence-based"],
  title: "Parlodel",
  subtitle: "Bromocriptine - dopamine agonist to lower prolactin",
  body: {
    genericName: "Bromocriptine mesylate",
    drugClass: "Dopamine agonist",
    purpose:
      "Lowers elevated prolactin levels (hyperprolactinemia) which can suppress ovulation and cause irregular or absent periods. By activating dopamine receptors in the pituitary gland, it reduces prolactin secretion and restores normal ovulatory cycles. An alternative to cabergoline, sometimes preferred because of its shorter half-life.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "1.25-7.5 mg daily, starting low (1.25 mg at bedtime) and gradually increasing to minimize side effects",
    howToTake:
      "Step 1: Prepare - Confirm your starting dose with your clinic. Have food ready to take with each dose, as taking bromocriptine on an empty stomach significantly worsens nausea. | Step 2: Take medication - Take with food to reduce nausea. Start with half a tablet (1.25 mg) at bedtime for the first week, then increase gradually as directed by your doctor. Most patients eventually take 2.5-5 mg daily in divided doses. | Step 3: After taking - Continue taking as prescribed until your prolactin levels normalize and ovulation resumes. Stand up slowly after lying or sitting, as bromocriptine can cause a drop in blood pressure. | ⚠ Missed dose - Take the missed dose as soon as you remember, as long as it is not close to the time of your next scheduled dose. Do not double up. Contact your clinic if you miss multiple doses, as consistent dosing is important for prolactin control. | ⚠ When to call clinic - Call if you experience severe dizziness or fainting, worsening nausea that prevents you from taking the medication, unusual mood changes, or if your menstrual cycles or symptoms do not improve after several weeks of treatment.",
    sideEffects: [
      "Nausea (most common - improves with food and gradual dose increase)",
      "Dizziness and lightheadedness",
      "Drowsiness",
      "Headache",
      "Nasal congestion",
      "Constipation",
      "Orthostatic hypotension (stand up slowly)",
    ],
    storage:
      "Store at room temperature (15-30°C / 59-86°F). Protect from light.",
    brandsByCountry: [
      { country: "US", brands: ["Parlodel", "Cycloset"] },
      { country: "UK", brands: ["Parlodel", "bromocriptine"] },
      { country: "EU", brands: ["Parlodel", "Pravidel"] },
      { country: "AU", brands: ["Parlodel", "Kripton"] },
      {
        country: "FR",
        brands: ["Parlodel", "Bromokin"],
        cisByBrand: {
          Parlodel: "66729097",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (TGA, KEGG, MIMS Asia, Novartis).
    // Cycloset is a US-only diabetes formulation.
    availability: [
      {
        name: "Parlodel",
        manufacturer: "Novartis",
        countries: ["US", "GB", "EU", "AU", "PH", "TH", "JP", "BR"],
      },
      { name: "Cycloset", manufacturer: "VeroScience", countries: ["US"] },
    ],
    aliases: ["Bromocriptine", "Bromocriptine mésylate", "Pravidel"],
    forWhom: "her",
    frBody: {
      genericName: "Mésylate de bromocriptine",
      drugClass: "Agoniste dopaminergique",
      purpose:
        "Diminue un taux de prolactine élevé (hyperprolactinémie) qui peut bloquer l'ovulation et entraîner des cycles irréguliers ou une aménorrhée. En activant les récepteurs dopaminergiques de l'hypophyse, la bromocriptine réduit la sécrétion de prolactine et permet le retour à des cycles ovulatoires normaux. Alternative à la cabergoline, parfois privilégiée pour sa demi-vie plus courte.",
      dosageRanges:
        "1,25 à 7,5 mg par jour, en commençant à dose faible (1,25 mg au coucher) puis en augmentant progressivement pour limiter les effets indésirables.",
      howToTake:
        "Préparer - Confirmez la dose initiale avec votre centre. Préparez un repas à prendre avec chaque dose, car prendre la bromocriptine à jeun aggrave fortement les nausées. | Prendre le médicament - Prenez avec de la nourriture pour réduire les nausées. Commencez par un demi-comprimé (1,25 mg) au coucher pendant la première semaine, puis augmentez progressivement selon les indications de votre médecin. La plupart des patientes prennent à terme 2,5 à 5 mg par jour en doses fractionnées. | Après la prise - Poursuivez le traitement tel que prescrit jusqu'à normalisation de la prolactine et reprise de l'ovulation. Levez-vous lentement après être restée allongée ou assise, car la bromocriptine peut entraîner une baisse de tension. | ⚠ Dose oubliée - Prenez la dose oubliée dès que vous y pensez, sauf si l'heure de la dose suivante est proche. Ne doublez jamais la dose. Contactez votre centre en cas d'oublis multiples, la régularité de la prise étant importante pour le contrôle de la prolactine. | ⚠ Quand contacter le centre - Contactez votre centre en cas de vertiges sévères ou de malaise, de nausées qui empêchent la prise du traitement, de changements d'humeur inhabituels ou si vos cycles ou symptômes ne s'améliorent pas après plusieurs semaines de traitement.",
      sideEffects: [
        "Nausées (le plus fréquent - s'améliore avec la prise alimentaire et l'augmentation progressive)",
        "Vertiges et étourdissements",
        "Somnolence",
        "Maux de tête",
        "Congestion nasale",
        "Constipation",
        "Hypotension orthostatique (se lever lentement)",
      ],
      storage:
        "Conservez à température ambiante (15-30 °C). À protéger de la lumière.",
    },
  },
};

registerBlock(bromocriptine);
